import React, { createContext, useContext, useState } from "react";
import type { AmlDetails } from "../types/aml.type";

export interface SessionStateType {
  amlDetails?: AmlDetails;
  isAmlCheck?: boolean;
  searchType?: string;
  url?: string;
  hitId?: string;
  searchId?: string;
}

export interface SessionContextType {
  sessionState?: SessionStateType | null;
  createProfile?: boolean;
  setCreateProfile?: (_value: boolean) => void;
  profileId?: string | null;
  setProfileId?: (_value: string | null) => void;
  titleName?: boolean;
  setTitleName?: (_value: boolean) => void;
  retryButton?: boolean;
  setRetryButton?: (_value: boolean) => void;
  disableButton?: boolean;
  setDisableButton?: (_value: boolean) => void;
}

export const useSession = (): SessionContextType | null =>
  useContext(SessionContext);

export const SessionContext = createContext<SessionContextType | null>(null);

const readAmlRedirectionState = (): SessionStateType | null => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return null;
  }

  try {
    const stored = sessionStorage.getItem("amlRedirectionState");
    if (!stored) {
      return null;
    }

    const parsedState = JSON.parse(stored) as SessionStateType;
    sessionStorage.removeItem("amlRedirectionState");
    return parsedState;
  } catch {
    return null;
  }
};

export const SessionProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [sessionState] = useState(() => readAmlRedirectionState());
  const [createProfile, setCreateProfile] = useState<boolean>(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [titleName, setTitleName] = useState<boolean>(
    () => sessionState?.isAmlCheck ?? false
  );
  const [retryButton, setRetryButton] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);

  const contextValue = React.useMemo(
    () => ({
      sessionState,
      createProfile,
      setCreateProfile,
      titleName,
      setTitleName,
      profileId,
      setProfileId,
      retryButton,
      setRetryButton,
      disableButton,
      setDisableButton,
    }),
    [
      sessionState,
      createProfile,
      setCreateProfile,
      titleName,
      setTitleName,
      profileId,
      setProfileId,
      retryButton,
      setRetryButton,
      disableButton,
      setDisableButton,
    ]
  );

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};
